import { ErrorCodeKeys } from '@easy-genomics/shared-lib/src/app/types/easy-genomics/errors';
import { getAuthToken, getRefreshedAuthToken, refreshUserData } from '@/lib/auth-helpers';

class HttpClient {
  private baseApiUrl = import.meta.env.VITE_BASE_API_URL || '';
  private defaultApiUrl = `${this.baseApiUrl}/easy-genomics`;
  private nfTowerApiUrl = `${this.baseApiUrl}/nf-tower`;
  private omicsApiUrl = `${this.baseApiUrl}/aws-healthomics`;

  private isRefreshingToken = false;
  private tokenRefreshPromise: Promise<string> | null = null;

  call<T>(method = 'GET', url: string, data: unknown = '', shouldRefresh: boolean = false): Promise<T | undefined> {
    return this.performRequest<T>(method, this.defaultApiUrl + url, data, shouldRefresh);
  }

  callSeqera<T>(
    method = 'GET',
    url: string,
    data: unknown = '',
    shouldRefresh: boolean = false,
  ): Promise<T | undefined> {
    return this.performRequest<T>(method, this.nfTowerApiUrl + url, data, shouldRefresh);
  }

  callOmics<T>(
    method = 'GET',
    url: string,
    data: unknown = '',
    shouldRefresh: boolean = false,
  ): Promise<T | undefined> {
    return this.performRequest<T>(method, this.omicsApiUrl + url, data, shouldRefresh);
  }

  callAwsBatch<T>(
    baseUrl: string,
    method = 'GET',
    url: string,
    data: unknown = '',
    shouldRefresh: boolean = false,
  ): Promise<T | undefined> {
    return this.performRequest<T>(method, baseUrl + url, data, shouldRefresh);
  }

  private async performRequest<T>(
    method: string,
    url: string,
    data: unknown = '',
    shouldRefresh: boolean,
  ): Promise<T | undefined> {
    try {
      const headers: HeadersInit = new Headers();
      headers.append('Content-Type', 'application/json');

      let token: string | undefined;
      try {
        token = shouldRefresh ? await this.refreshToken() : await getAuthToken();
      } catch (tokenError) {
        console.warn(`Failed to get token; reason: ${tokenError}; continuing without Bearer or X-API header`);
      }
      if (token) {
        headers.append('Authorization', `Bearer ${token}`);
      }

      const settings: RequestInit = { method, headers };
      if (data) {
        settings.body = JSON.stringify(data);
      }

      const response = await fetch(url, settings);
      if (!response.ok) {
        await this.handleResponseError(response);
      }

      if (shouldRefresh && token) {
        await refreshUserData();
        const { useUiStore } = await import('@/stores/ui');
        useUiStore.getState().incrementRemountAppKey();
      }

      const jsonResponse = await response.json();
      return jsonResponse as T;
    } catch (error: any) {
      if (error.message === 'EG-110') {
        return this.performRequest(method, url, data, true);
      }
      throw new Error(`Request error: ${error.message}`);
    }
  }

  private async handleResponseError(response: Response): Promise<void> {
    let errorMessage = `HTTP error! status: ${response.status}`;
    let errorCode: ErrorCodeKeys | undefined;

    try {
      const errorBody: { Error: string; ErrorCode: ErrorCodeKeys } = await response.json();
      errorMessage = errorBody.Error || errorMessage;
      errorCode = errorBody.ErrorCode;
    } catch (error) {
      console.error('Error parsing response body', error);
    }

    if (errorCode === 'EG-110') {
      throw new Error('EG-110');
    } else {
      throw new Error(errorMessage);
    }
  }

  private refreshToken(): Promise<string> {
    if (this.isRefreshingToken) {
      return this.tokenRefreshPromise!;
    }

    this.isRefreshingToken = true;
    this.tokenRefreshPromise = getRefreshedAuthToken().finally(() => {
      this.isRefreshingToken = false;
      this.tokenRefreshPromise = null;
    });

    return this.tokenRefreshPromise;
  }
}

export const httpClient = new HttpClient();
